import { RobotOutlined, UserOutlined } from "@ant-design/icons";
import type { AgenticClient } from "@p0security/integrations-agentic-shared/interface";
import type { RequestAttributionInput } from "@p0security/shared/permission-requests/attribution";
import { Evidence } from "@p0security/shared/types/evidence";
import { Typography } from "@p0security/ui";
import {
  formatMcpClientPlatform,
  mcpClientPlatformIcon,
} from "components/Agentic/clientPlatforms";
import { Identity } from "components/common/Identity";
import React, { useContext, useMemo } from "react";

import { AgenticClientsContext } from "../../../Agentic/AgenticClientsContext.tsx";
import { IconWithBadge } from "./IconWithBadge";
import { UserCell } from "./UserCell";

export const RequestorCell: React.FC<{
  clientId?: string;
  attribution?: RequestAttributionInput;
  preApproval?: readonly [Evidence["source"], string] | "unknown";
}> = ({ clientId, preApproval, attribution }) => {
  const { clientsById } = useContext(AgenticClientsContext);

  const client: AgenticClient | undefined = useMemo(
    () => (clientId ? clientsById.get(clientId) : undefined),
    [clientId, clientsById]
  );

  if (!attribution) return null;

  const { requestor, viaAgents } = attribution;
  const { type, sub } = requestor;

  const platform =
    client?.type === "client_credential_post" ? client.platform : undefined;
  const agentIcon = platform ? (
    mcpClientPlatformIcon(platform)
  ) : (
    <RobotOutlined />
  );
  const agentTooltip = platform
    ? `Agentic request (${formatMcpClientPlatform(platform)})`
    : "Agentic request";

  return (
    <UserCell
      icon={
        <IconWithBadge
          icon={client ? agentIcon : <UserOutlined />}
          tooltip={client ? agentTooltip : "Human request"}
        />
      }
      username={
        <Identity value={{ type, sub: sub, role: "principal" }} noLink />
      }
      subtitle={
        <Typography.Text className="sub-text" type="secondary" ellipsis>
          {viaAgents.length ? (
            <Identity
              className="text-s"
              noLink
              value={{ type: "agent", sub: viaAgents[0], role: "delegate" }}
            />
          ) : client ? (
            "Headless agent"
          ) : (
            "Human identity"
          )}
        </Typography.Text>
      }
      preApproval={preApproval}
    />
  );
};
